import React, {useEffect, useState} from 'react';
import {Alert, Button, Checkbox, Form, InputNumber, message, Select} from "antd";
import {WarningOutlined} from "@ant-design/icons";
import { useTranslation } from '../../hooks/useTranslation';
import { isEnglishSite } from '../../config/site';

// 兜底域名选项（当远程获取失败时使用）
const fallbackHostOptions = [
  { value: 'edgeone.51shazhu.com', label: 'edgeone.51shazhu.com（不推荐)' },
  { value: 'dl.z.wiki', label: 'dl.z.wiki (不推荐)' },
  { value: 'tc.z.wiki', label: 'tc.z.wiki (不推荐)' },
  { value: 'y.z.wiki', label: 'y.z.wiki (阿里云节点,不推荐)' },
  { value: 'tc-new.z.wiki', label: 'tc-new.z.wiki (国内域名)' },
  { value: 'img.51shazhu.com', label: 'img.51shazhu.com（国内域名）' },
  { value: 'a.tuchuangyun.top', label: 'a.tuchuangyun.top (国外节点)' },
  { value: 'jp.tuchuangyun.top', label: 'jp.tuchuangyun.top (仅国外可访问，国内无法访问)' },
  { value: 'xland.eu.org', label: 'xland.eu.org (国外很快，国内速度一般)' },
];

// 远程域名配置地址
const REMOTE_DOMAINS_URL = 'https://tc-new.z.wiki/autoupload/fdd/config/domains.json';

const Config: React.FC = () => {
  const { t, currentLanguage, changeLanguage } = useTranslation();
  const [config, setConfig] = useState<Record<string, string>>({});
  const [bigFileUpload, setBigFileUpload] = useState(false);
  const [isActiveVip, setIsActiveVip] = useState(false);
  const [lifecycleDays, setLifecycleDays] = useState<number | null>(null);
  const [hostOptions, setHostOptions] = useState<Array<{value: string, label: string}>>([]);
  const [defaultDomains, setDefaultDomains] = useState<Array<{value: string, label: string}>>(fallbackHostOptions);

  // 获取远程默认域名配置和用户信息
  useEffect(() => {
    const fetchData = async () => {
      // 先获取远程域名配置
      let domains = fallbackHostOptions;
      try {
        const urlWithTimestamp = `${REMOTE_DOMAINS_URL}?t=${Date.now()}`;
        const res = await fetch(urlWithTimestamp, {
          method: 'GET',
          mode: 'cors',
        });
        const remoteDomains = await res.json();
        console.info('成功获取远程域名配置', remoteDomains);
        if (Array.isArray(remoteDomains) && remoteDomains.length > 0) {
          domains = remoteDomains;
        } else {
          console.warn('远程域名配置格式不正确，使用兜底数据');
        }
      } catch (error) {
        console.error('获取远程域名配置失败，使用兜底数据', error);
      }

      // 设置默认域名
      setDefaultDomains(domains);

      // 再获取用户信息并合并域名
      try {
        const infoRes = await fetch('/login/info', {
          method: 'GET',
        });
        const e = await infoRes.json();
        console.info(e);
        if (e.isSuccess) {
          const configData = JSON.parse(e.detail?.config || '{}');
          setConfig(configData);
          setIsActiveVip(e.detail?.isActiveVip || false);
          setLifecycleDays(configData.lifecycleDays ? parseInt(configData.lifecycleDays) : null);
          
          // 处理域名列表：合并默认域名和用户配置域名
          const userDomains = e.detail?.domains || [];
          const userDomainOptions = userDomains.map((domain: string) => ({
            value: domain,
            label: domain
          }));
          
          // 合并默认域名和用户配置域名，去重
          const allOptions = [...domains, ...userDomainOptions];
          setHostOptions(allOptions);
        }
      } catch (error) {
        console.error('获取用户信息失败', error);
      }
    };

    fetchData();
  }, [])

  const [saving, setSaving] = useState(false)

  const handleLanguageChange = async (value: 'zh' | 'en') => {
    await changeLanguage(value);
    // 语言切换后刷新页面以应用所有更改
    window.location.reload();
  };

  const handleSave = () => {
    console.info('handleSave', config)
    setSaving(true);
    
    // 更新配置对象包含生命周期天数
    const updatedConfig = {
      ...config,
      lifecycleDays: lifecycleDays?.toString() || ''
    };
    
    fetch('/admin/config/update', {
      method: 'POST',
      body: JSON.stringify(updatedConfig),
      headers: {
        'Content-Type': 'application/json;charset=utf8'
      }
    }).then(res => res.json()).then((data) => {
      console.info('flag is', data);
      if (data.success) {
        message.info(t('config.configUpdated'))
      } else {
        message.error(t('config.configUpdateFailed'))
      }
    }).finally(() => {
      setSaving(false);
    })
  }

  // 检查是否登录（通过检查是否有配置数据）
  const isLoggedIn = config && Object.keys(config).length > 0;

  return (
    <>
      <Form>
        {/* 语言选择 - 英文站（postanything.net）不展示 */}
        {!isEnglishSite() && (
          <Form.Item label={t('config.language')}>
            <Select
              placeholder={t('config.languagePlaceholder')}
              value={currentLanguage}
              onChange={handleLanguageChange}
              style={{ width: '100%' }}
              options={[
                { value: 'zh', label: t('config.chinese') },
                { value: 'en', label: t('config.english') },
              ]}
            />
          </Form.Item>
        )}

        {/* VIP 相关配置 - 仅登录用户可见 */}
        {isLoggedIn && (
          <>
            <Alert message={t('config.vipOnly')} icon={<WarningOutlined/>} type="error" style={{marginBottom: '8px'}} showIcon/>

            {/* <Form.Item label="水印内容">
              <Input placeholder="请输入水印内容，最多20个字符串" value={config.watermask}
                     onChange={(e) => setConfig({...config, watermask: e.target.value.slice(0, 20)})}/>
            </Form.Item> */}
            {
              isActiveVip && (
                <Form.Item label={t('config.domainSelection')}>
                  <Select
                    placeholder={t('config.domainPlaceholder')}
                    value={config.host}
                    onChange={(value) => setConfig({...config, host: value})}
                    options={hostOptions}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              )
            }
            
            {
              isActiveVip && (
                <Form.Item label={t('config.lifecycleSettings')}>
                  <InputNumber
                    placeholder={t('config.lifecyclePlaceholder')}
                    min={1}
                    max={99999999999}
                    value={lifecycleDays}
                    onChange={(value) => setLifecycleDays(value)}
                    addonAfter={t('common.days')}
                    style={{ width: '100%' }}
                  />
                  <div style={{ fontSize: '12px', color: '#ff4d4f', marginTop: '4px' }}>
                    {t('config.lifecycleWarning')}
                  </div>
                </Form.Item>
              )
            }
            {
              config.allowJsUpload && <Form.Item label={t('config.bigFileUpload')}>
                <Checkbox
                  checked={bigFileUpload}
                  onChange={(e) => {
                    console.info('e', e.target.checked, config)
                    setBigFileUpload(e.target.checked);
                    (window as any)._debug_ = e.target.checked;
                  }}
                >{t('config.open')}</Checkbox>
              </Form.Item>
            }

            {/* 保存按钮 - 仅登录用户可见 */}
            <Form.Item>
              <Button type="primary" loading={saving} onClick={handleSave}>{t('config.save')}</Button>
            </Form.Item>
          </>
        )}
      </Form>
    </>
  );
};

export default Config;